import * as ko from "knockout";

import { UnmappableLocation, ErrorLevel } from "./api";
import { MapProgressTooltip } from "./mapProgressTooltip";


/**
 * The state of a MapProgressIndicator.
 */
export enum MapProgressState {
    /**
     * Default state.
     */
    None,
    /**
     * An unexpected error was encountered.
     */
    UnexpectedError,
    /**
     * A single location has an error.
     */
    Error,
    /**
     * A single location has a warning.
     */
    Warning,
    /**
     * Multiple locations have errors and possibly warnings.
     */
    MultipleErrors,
    /**
     * Multiple locations have warnings (but not errors).
     */
    MultipleWarnings
}


/**
 * Options to supply when creating a MapProgressIndicator.
 */
export interface MapProgressOptions {
    /**
     * A display name for the plural version of the item types being displayed on the map.
     * @default "Locations"
     */
    pluralEntityType?: string;
    /**
     * A callback to refresh the map with new data from the server.
     */
    refreshMap: () => void;
    /**
     * "true" if the progress indicator and tooltip should be hidden (regardless of state), "false" otherwise.
     */
    hide: ko.Computed<boolean>;
}


/**
 * Data to load into a @see MapProgressIndicator.
 */
export interface MapProgressRecord {
    /**
     * "true" if the api call encountered an unknown error, "false" otherwise.
     */
    hasGenericError: boolean;
    /**
     * The unmappable locations returned from the api call.
     */
    unmappableLocations: UnmappableLocation[];
    /**
     * "true" when the api call is currently executing, "false" otherwise.
     */
    isBusy: boolean;
    /**
     * "true" if the map has multiple locations (show plural errors), "false" otherwise (show singular errors).
     */
    hasMultipleLocations: boolean;
}


/**
 * A view model for displaying a map progress indicator.
 */
export class MapProgressIndicator {
    /**
     * Creates a new instance of the MapProgressIndicator class.
     */
    constructor(options: MapProgressOptions) {
        if (!options) { throw new Error("options is required."); }
        if (typeof options.refreshMap !== "function") { throw new Error("options.refreshMap must be a function."); }
        if (!ko.isObservable(options.hide)) { throw new Error("options.hide must be an observable."); }

        this.errorsTooltip = new MapProgressTooltip();
        this.isBusy = ko.observable(false);
        this._closed = ko.observable(false);
        this._hasError = ko.observable(false);
        this._hasMultipleLocations = ko.observable(false);
        this._refreshMap = options.refreshMap;

        this.state = ko.pureComputed(() => this._computeState());
        this.css = ko.pureComputed(() => this._computeCss());
        this.buttonText = ko.pureComputed(() => this._computeButtonText());
        this.title = ko.pureComputed(() => this._computeTitle(options.pluralEntityType));
        this.subtitle = ko.pureComputed(() => this._computeSubtitle());

        this.show = ko.pureComputed(() => {
            return (this.isBusy() || this.state() !== MapProgressState.None) && options.hide() === false && !this._closed();
        });

        this.show.subscribe((newValue: boolean) => {
            if (!newValue) {
                // If hiding the progress indicator, hide the tooltip.
                this.errorsTooltip.hide();
            }
        });

        ko.computed(() => this._hideOrShowTooltip());
        ko.computed(() => this._resetClosedState());
    }


    /**
     * Loads the given data into the progress indicator.
     * @param {MapProgressRecord} record The data to load.
     */
    loadViewModel(record: MapProgressRecord): void {
        if (record) {
            this.isBusy(record.isBusy);
            this._hasError(record.hasGenericError);
            this._hasMultipleLocations(record.hasMultipleLocations);
            this.errorsTooltip.loadViewModel(record.unmappableLocations);
        }
    }


    /**
     * The action to perform when the progress indicator button is clicked.
     */
    onButtonClick(): boolean {
        switch (this.state()) {
            case MapProgressState.Error:
                // Edit Location
                this.errorsTooltip.unmappableLocations()[0].openItem();
                return false;
            case MapProgressState.MultipleErrors:
            case MapProgressState.MultipleWarnings:
                // Why ?
                // Tooltip open handled by pliMapTooltipButton binding
                return false;
            default:
                // Try Again
                this._refreshMap();
                return false;
        }
    }


    /**
     * Hides the progress indicator.
     */
    onCloseClick(): boolean {
        this._closed(true);
        return false;
    };


    private _computeButtonText(): string {
        switch (this.state()) {
            case MapProgressState.Error:
                return "Fix";
            case MapProgressState.MultipleErrors:
            case MapProgressState.MultipleWarnings:
                return "Why?";
            default:
                return "Try Again";
        }
    }


    private _computeCss(): string {
        switch (this.state()) {
            case MapProgressState.Error:
                return "error showButtonBar showEditButtonIcon";
            case MapProgressState.MultipleErrors:
                return "error showButtonBar showPopupButtonIcon";
            case MapProgressState.UnexpectedError:
                return "error showButtonBar showRefreshButtonIcon";
            case MapProgressState.MultipleWarnings:
                return "warning showButtonBar showPopupButtonIcon";
            case MapProgressState.Warning:
                return "warning";
            default:
                return "";
        }
    }


    private _computeState(): MapProgressState {
        if (this.isBusy()) {
            return MapProgressState.None;
        }

        if (this._hasError()) {
            return MapProgressState.UnexpectedError;
        }

        const unmappableLocations = this.errorsTooltip.unmappableLocations();

        if (unmappableLocations && unmappableLocations.length) {
            const hasErrors = unmappableLocations.some((location: UnmappableLocation) => {
                return location.errorLevel === ErrorLevel.Error;
            });

            if (hasErrors) {
                return unmappableLocations.length > 1 ? MapProgressState.MultipleErrors : MapProgressState.Error;
            }

            const hasWarnings = unmappableLocations.some((location: UnmappableLocation) => {
                return location.errorLevel === ErrorLevel.Warning;
            });

            if (hasWarnings) {
                return unmappableLocations.length > 1 ? MapProgressState.MultipleWarnings : MapProgressState.Warning;
            }
        }

        return MapProgressState.None;
    }


    private _computeSubtitle(): string {
        const state = this.state();

        if (this.isBusy()) {
            return "Please wait...";
        }

        switch (state) {
            case MapProgressState.UnexpectedError:
                return "Sorry there was an unexpected error while loading the map";
            case MapProgressState.Error:
            case MapProgressState.Warning:
                return this.errorsTooltip.unmappableLocations()[0].message;
            default:
                return "";
        }
    }


    private _computeTitle(pluralEntityType?: string): string {
        const state = this.state();

        if (this.isBusy()) {
            return "LOADING";
        }
        if (state === MapProgressState.UnexpectedError) {
            return "UNEXPECTED ERROR";
        }
        if (state === MapProgressState.None) {
            return "";
        }

        if (this._hasMultipleLocations()) {
            pluralEntityType = !pluralEntityType ? "LOCATIONS" : pluralEntityType.toUpperCase();

            switch (state) {
                case MapProgressState.Error:
                case MapProgressState.Warning:
                    return this.errorsTooltip.unmappableLocations()[0].titleDisplayName.toUpperCase() + " - COULD NOT BE DISPLAYED";
                case MapProgressState.MultipleErrors:
                case MapProgressState.MultipleWarnings:
                    return "MULTIPLE " + pluralEntityType + " COULD NOT BE DISPLAYED";
                default:
                    break;
            }
        }

        return "LOCATION COULD NOT BE DISPLAYED";
    }


    private _hideOrShowTooltip(): void {
        const state = this.state();
        const isBusy = this.isBusy();

        if (isBusy) {
            this.errorsTooltip.disable();
            return;
        }

        switch (state) {
            case MapProgressState.MultipleErrors:
            case MapProgressState.MultipleWarnings:
                this.errorsTooltip.enable();
                return;
            default:
                this.errorsTooltip.disable();
                return;
        }
    }


    private _resetClosedState(): void {
        // Subscribe to data changes
        this.errorsTooltip.unmappableLocations();

        // Subscribe to isBusy
        this.isBusy();

        this._closed(false);
    }


    /**
     * The button text to show on the progress indicator button.
     */
    buttonText: ko.Computed<string>;
    /**
     * The css class(es) to apply to the progress indicator.
     */
    css: ko.Computed<string>;
    /**
     * The tooltip to show when there are multiple unmappable locations.
     */
    errorsTooltip: MapProgressTooltip;
    /**
     * "true" if the progress indicator is busy, "false" otherwise.
     */
    isBusy: ko.Observable<boolean>;
    /**
     * true if the progress indicator should be shown, false otherwise.
     */
    show: ko.Computed<boolean>;
    /**
     * The state of this MapProgressIndicator.
     */
    state: ko.Computed<MapProgressState>;
    /**
     * The subtitle to show for the progress indicator.
     */
    subtitle: ko.Computed<string>;
    /**
     * The title to show for the progress indicator.
     */
    title: ko.Computed<string>;


    private _closed: ko.Observable<boolean>;
    private _hasError: ko.Observable<boolean>;
    private _hasMultipleLocations: ko.Observable<boolean>;
    private _refreshMap: () => void;
 }



// WEBPACK FOOTER //
// mapProgressIndicator.ts